/**
 * Client edit — writes through Surface B (`management.clients`, session + CSRF).
 *
 * Edits contact + address details and the active flag. Agency membership is identity and is NOT editable
 * here (moving a client between agencies would strand its campaigns); the XML-PPC plumbing is left alone.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { management, ApiError } from '../lib/api';
import { Text, Check } from './CampaignForm';

type Form = Record<string, unknown>;
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export function ClientFormScreen(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [f, setF] = useState<Form>({
    company_name: '', email: '', phone: '', first_name: '', last_name: '',
    address1: '', address2: '', city: '', state: '', postal_code: '', country: '', is_active: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!id) return;
    let live = true;
    management.clients.get(id).then(({ client: c }) => {
      if (!live) return;
      setF({
        company_name: str(c.company_name), email: str(c.email), phone: str(c.phone),
        first_name: str(c.first_name), last_name: str(c.last_name),
        address1: str(c.address1), address2: str(c.address2), city: str(c.city),
        state: str(c.state), postal_code: str(c.postal_code), country: str(c.country),
        is_active: c.is_active === 'Y' || c.is_active === true,
      });
      setLoading(false);
    }).catch((e) => { setError(e instanceof ApiError ? e.message : 'Could not load the client.'); setLoading(false); });
    return () => { live = false; };
  }, [id]);

  const submit = useCallback(async () => {
    setError(null);
    if (!str(f.company_name).trim()) { setError('Company name is required.'); return; }
    setSaving(true);
    const body: Form = {
      company_name: f.company_name, email: f.email, phone: f.phone,
      first_name: f.first_name, last_name: f.last_name,
      address1: f.address1, address2: f.address2, city: f.city,
      state: f.state, postal_code: f.postal_code, country: f.country,
      is_active: f.is_active ? 'Y' : 'N',
    };
    try {
      await management.clients.update(id, body);
      navigate(`/clients/${id}`);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Save failed.'); setSaving(false); }
  }, [f, id, navigate]);

  if (loading) return <div className="busy">Loading…</div>;

  return (
    <div className="mform">
      <h1>Edit client</h1>
      <p className="sub">Client details write to the reporting store. Agency membership is fixed here.</p>
      {error && <div className="banner error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <section>
        <h2>Company</h2>
        <div className="mgrid">
          <Text f={f} set={set} k="company_name" label="Company name" full />
          <Text f={f} set={set} k="email" label="Email" type="email" />
          <Text f={f} set={set} k="phone" label="Phone" />
          <Text f={f} set={set} k="first_name" label="Contact first name" />
          <Text f={f} set={set} k="last_name" label="Contact last name" />
          <Check f={f} set={set} k="is_active" label="Active" />
        </div>
      </section>

      <section>
        <h2>Address</h2>
        <div className="mgrid">
          <Text f={f} set={set} k="address1" label="Address line 1" full />
          <Text f={f} set={set} k="address2" label="Address line 2" full />
          <Text f={f} set={set} k="city" label="City" />
          <Text f={f} set={set} k="state" label="State" />
          <Text f={f} set={set} k="postal_code" label="Postal code" />
          <Text f={f} set={set} k="country" label="Country" />
        </div>
      </section>

      <div className="mform-actions">
        <button className="button primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        <button className="button" onClick={() => navigate(`/clients/${id}`)}>Cancel</button>
      </div>
    </div>
  );
}
